import React from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from 'react-query';
import * as routes from 'pages/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from './index.form';
import { useForm } from 'react-hook-form';
import ModalManager, { useModal } from '@ebay/nice-modal-react';
import { Pane, Text, IconButton, CrossIcon, WarningSignIcon, Tooltip, Position, Button as EvergreenButton } from 'evergreen-ui';
import { GoogleIcon } from 'public/icons';
import Input from 'UI/Input';
import Modal from 'UI/Modal';
import Button from 'UI/Button';
import { useSignin, useSigninWithGoogle, useLogout } from 'resources/auth/hooks';
import uiNotifications from 'services/uiNotification.service';
import styles from './styles.module.scss';

const SignInModal = ModalManager.create(() => {
  const modal = useModal();
  const router = useRouter();
  const queryClient = useQueryClient();
  const currentUser = queryClient.getQueryData(['user.currentUser']);
  const currentUserEmail = currentUser && currentUser.email ? currentUser.email : null;

  const { mutateAsync: signin, isLoading } = useSignin();
  const { mutateAsync: signinWithGoogle } = useSigninWithGoogle();
  const { mutateAsync: logout } = useLogout();

  const { handleSubmit, formState: { errors }, control } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    shouldUnregister: true,
    defaultValues: {
      email: currentUserEmail,
      password: '',
    },
  });

  const onClose = async () => {
    await logout();
    modal.hide();
    router.push(routes.path.signIn);
  }

  const onSignIn = async (credentials) => {
    try {
      const response = await signin(credentials);
      const isSignInFailed = !response || response.isBadRequest;
      if (isSignInFailed) return;

      modal.hide();
    } catch (error) {
      uiNotifications.showExtractedError(error);
    }
  };

  const onSignInWithGoogle = async () => {
    await signinWithGoogle();
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={onClose}
      onAfterClose={modal.remove}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      className={styles.modal}
    >

      {/* Close button */}
      <Pane display="flex" justifyContent="flex-end" padding={16}>
        <Tooltip content="Closing this window will discard unsaved data and send you to Sign In page." position={Position.BOTTOM}>
          <IconButton onClick={onClose} icon={CrossIcon} color="var(--gray-400)" appearance="minimal" />
        </Tooltip>
      </Pane>

      <Pane display="flex" flexDirection="column" paddingX={64} paddingBottom={64}>

        {/* Sign In with email and password form */}
        <form onSubmit={handleSubmit(onSignIn)}>
          <Pane display="flex" alignItems="center">
            <WarningSignIcon color="warning" marginRight={8} />
            <Text fontWeight={400} fontSize={24} lineHeight="24px" color="var(--gray-600)" fontFamily="'Zilla Slab', serif">
              Session Expired
            </Text>
          </Pane>

          <Pane marginTop={4}>
            <Text fontWeight={300} fontSize={14} lineHeight="20px" color="var(--gray-600)" fontFamily="'Merriweather Sans', sans-serif">
              Please sign in again to continue
            </Text>
          </Pane>

          <Pane marginTop={12} width={80} height={2} backgroundColor="var(--primary)" />

          <Pane marginTop={24}>
            <Input
              label="Email*"
              name="email"
              control={control}
              error={errors.email}
              margin={0}
              disabled={isLoading || !!currentUserEmail}
            />
          </Pane>

          <Pane marginTop={24}>
            <Input
              label="Password*"
              name="password"
              type="password"
              control={control}
              error={errors.password}
              margin={0}
              disabled={isLoading}
            />
          </Pane>

          <Pane marginTop={40}>
            <Button
              type="primary"
              loading={isLoading}
              htmlType="submit"
            >
              Sign In
            </Button>
          </Pane>
        </form>

        {/* Alternative Sign In */}
        <Pane display="flex" alignItems="center" justifyContent="center" gap={24} marginY={24}>
          <Pane width={68} height={1} backgroundColor="var(--gray-400)"></Pane>
          <Text fontWeight={300} fontSize={14} lineHeight="20px" color="var(--gray-500)" fontFamily="'Merriweather Sans', sans-serif">
            OR
          </Text>
          <Pane width={68} height={1} backgroundColor="var(--gray-400)"></Pane>
        </Pane>
        <Pane>
          <Tooltip content="Signing In with Google will discard unsaved data." position={Position.BOTTOM}>
            <EvergreenButton intent="none" width="100%" height={40} iconBefore={GoogleIcon} onClick={onSignInWithGoogle}>
              <Text fontWeight={300} fontSize={14} lineHeight="20px" color="var(--neutral-600)" fontFamily="'Merriweather Sans', sans-serif">
                Sign In with Google
              </Text>
            </EvergreenButton>
          </Tooltip>
        </Pane>

      </Pane>

    </Modal >
  );
});

ModalManager.register('signin-modal', SignInModal);

export default React.memo(SignInModal);
